import type { LucideIcon } from 'lucide-react'
import {
  CircleCheck,
  Dock,
  Ellipsis,
  EllipsisVertical,
  MonitorDown,
  Share,
  SquarePlus,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { InstallPlatform } from '../../utils/install-prompt'

interface Step {
  icon: LucideIcon
  key: string
}

// Where the button is, browser by browser. Safari has no install event, so on
// Apple's platforms these steps are the only way; on Android and desktop
// Chromium they stand in when the browser has not offered its own dialog.
// Shared by the install card and the push asks, which on iOS can only offer
// the install (see docs/PUSH_OPT_IN_PLAN.md).
const INSTALL_STEPS: Record<InstallPlatform, Step[]> = {
  'ios-safari': [
    { icon: Share, key: 'pwa.install.steps.iosSafari.share' },
    { icon: SquarePlus, key: 'pwa.install.steps.iosSafari.addToHome' },
    { icon: CircleCheck, key: 'pwa.install.steps.iosSafari.confirm' },
  ],
  'ios-chrome': [
    { icon: Share, key: 'pwa.install.steps.iosChrome.share' },
    { icon: SquarePlus, key: 'pwa.install.steps.iosChrome.addToHome' },
    { icon: CircleCheck, key: 'pwa.install.steps.iosChrome.confirm' },
  ],
  'ios-other': [
    { icon: Share, key: 'pwa.install.steps.iosOther.share' },
    { icon: SquarePlus, key: 'pwa.install.steps.iosOther.addToHome' },
    { icon: Ellipsis, key: 'pwa.install.steps.iosOther.safari' },
  ],
  'macos-safari': [
    { icon: Share, key: 'pwa.install.steps.macosSafari.menu' },
    { icon: Dock, key: 'pwa.install.steps.macosSafari.addToDock' },
    { icon: CircleCheck, key: 'pwa.install.steps.macosSafari.confirm' },
  ],
  android: [
    { icon: EllipsisVertical, key: 'pwa.install.steps.android.menu' },
    { icon: SquarePlus, key: 'pwa.install.steps.android.install' },
    { icon: CircleCheck, key: 'pwa.install.steps.android.confirm' },
  ],
  'desktop-chromium': [
    { icon: MonitorDown, key: 'pwa.install.steps.desktopChromium.addressBar' },
    { icon: EllipsisVertical, key: 'pwa.install.steps.desktopChromium.menu' },
    { icon: CircleCheck, key: 'pwa.install.steps.desktopChromium.confirm' },
  ],
}

/** The numbered steps for one platform. */
export function InstallStepsList({ platform }: { platform: InstallPlatform }) {
  const { t } = useTranslation()

  return (
    <ol className="flex flex-col gap-3">
      {INSTALL_STEPS[platform].map(({ icon: Icon, key }, index) => (
        <li key={key} className="flex items-start gap-3 text-sm">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
            {index + 1}
          </span>
          <span className="flex-1 pt-1">{t(key)}</span>
          <Icon className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </li>
      ))}
    </ol>
  )
}
